import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";

const DeclareResult = () => {
  // ✅ 1. INDIAN TIME DATE SETTING
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`; 
  };

  // States
  const [date, setDate] = useState(getTodayDate());
  const [selectedGame, setSelectedGame] = useState("");
  const [panna, setPanna] = useState("");
  const [digit, setDigit] = useState("");
  const [search, setSearch] = useState(""); 
  const [gameOptions, setGameOptions] = useState([]);
  const [bidHistoryData, setBidHistoryData] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [deletingBid, setDeletingBid] = useState(null);
  const [declaring, setDeclaring] = useState(false);
  
  // Modal State
  const [showWinnerModal, setShowWinnerModal] = useState(false);

  // Game Result History States
  const [gameResultHistory, setGameResultHistory] = useState([]);
  const [loadingGameResult, setLoadingGameResult] = useState(false);
  const [searchGameResult, setSearchGameResult] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [gameResultDate, setGameResultDate] = useState(getTodayDate());

  // ✅ FETCH ALL GAMES (Fixed Logic)
  useEffect(() => {
    const fetchGameList = async () => {
      try {
        const response = await axiosInstance.get(`/api/starline/getGameList`);
        // Ensure we capture ALL games
        const allGames = response.data.data || [];
        // Extract unique names just in case
        const uniqueGameNames = [...new Set(allGames.map((game) => game.game_name))];
        setGameOptions(uniqueGameNames);
      } catch (error) {
        // console.error("Error fetching game list");
      }
    };
    fetchGameList();
    fetchGameResultHistory();
  }, []);

  // Fetch Game Result History
  const fetchGameResultHistory = async () => {
    setLoadingGameResult(true);
    try {
      const response = await axiosInstance.get(`/api/starline/game-result-history`);
      setGameResultHistory(response.data.data || []);
    } catch (error) {
      // console.error("Error fetching history");
    } finally {
      setLoadingGameResult(false);
    }
  };

  // Helper: Format Date
  const formatDateForBackend = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  // Helper: Panna & Digit Logic
  const handlePannaChange = (e) => {
    const selectedPanna = e.target.value;
    setPanna(selectedPanna);
    if (selectedPanna.length === 3) {
      const sum = selectedPanna.split("").reduce((acc, num) => acc + parseInt(num, 10), 0);
      setDigit(sum % 10);
    } else {
      setDigit("");
    }
  };

  // Show Winners Logic
  const handleShowWinners = async () => {
    if (!date || !selectedGame) {
      alert("Please select Date and Game Name first.");
      return;
    }

    setLoading(true);
    setBidHistoryData([]); 

    try {
      const payload = { 
        date: formatDateForBackend(date), 
        gamename: selectedGame, 
        panna, 
        digit 
      };

      const response = await axiosInstance.post(`/api/starlinebid/showWinnerList`, payload);
      const winners = response.data.results || response.data.winners || [];
      
      setBidHistoryData(winners);
      setShowWinnerModal(true); // Open Modal

    } catch (error) {
      alert("Failed to fetch winner list.");
    } finally {
      setLoading(false);
    }
  };

  // Declare Result Logic
  const handleDeclareResult = async () => {
    if (!date || !selectedGame || !panna || digit === "" || digit === null) {
      alert("Please fill all fields.");
      return;
    }

    setDeclaring(true);
    try {
      const payload = {
        date: formatDateForBackend(date),
        gamename: selectedGame,
        panna,
        digit
      };

      const response = await axiosInstance.post(`/api/starlinebid/declare-winners`, payload);

      if (response.data.success) {
        alert("Result declared successfully!");
        fetchGameResultHistory();
        setShowWinnerModal(false);
      } else {
        alert(response.data.message || "Failed to declare result");
      }
    } catch (error) {
      const msg = error.response?.data?.message || "Error occurred";
      alert(msg);
    } finally {
      setDeclaring(false);
    }
  };

  // Delete Bid
  const handleDeleteBid = async (bidId) => {
    if (!window.confirm("Delete this bid?")) return;
    setDeletingBid(bidId);
    try {
      const response = await axiosInstance.delete(`/api/starlinebid/deletebid/${bidId}`);
      if (response.data.status === "success") {
        setBidHistoryData((prev) => prev.filter((bid) => bid.bidId !== bidId));
        alert("Deleted successfully!");
      } else {
        alert("Failed to delete");
      }
    } catch (error) {
      // console.error(error);
    } finally {
      setDeletingBid(null);
    }
  };

  // Delete Result History
  const handleDeleteGameResult = async (resultId) => {
    if (!window.confirm("Delete this result?")) return;
    try {
      const response = await axiosInstance.delete(`/api/starline/delete-game-result/${resultId}`);
      if (response.data.success) {
        alert("Deleted successfully");
        fetchGameResultHistory();
      } else {
        alert("Failed to delete");
      }
    } catch (error) {
       // console.error(error);
    }
  };

  // Pagination & Filtering
  const formattedGameResultDate = formatDateForBackend(gameResultDate);
  const filteredGameResults = gameResultHistory.filter((result) =>
    result.date === formattedGameResultDate &&
    (
      result.market.toLowerCase().includes(searchGameResult.toLowerCase()) ||
      (result.panna && result.panna.includes(searchGameResult)) ||
      result.digit.toString().includes(searchGameResult)
    )
  );

  const paginatedResults = filteredGameResults.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  return (
    <div className="p-4 md:p-6 w-full min-h-screen bg-gray-50">
      <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gray-800 border-l-4 border-blue-600 pl-4">
        Declare Result
      </h2>

      {/* --- RESPONSIVE INPUT SECTION --- */}
      <div className="bg-white p-4 md:p-6 shadow-lg rounded-xl mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          
          {/* Date */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-600 mb-1">Result Date</label>
            <input
              type="date"
              className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          {/* Game Dropdown (Should show all games) */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-600 mb-1">Game Name</label>
            <select
              className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={selectedGame}
              onChange={(e) => setSelectedGame(e.target.value)}
            >
              <option value="">-- Select Game --</option>
              {gameOptions.map((game, index) => (
                <option key={index} value={game}>{game}</option>
              ))}
            </select>
          </div>

          {/* Panna */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-600 mb-1">Panna</label>
            <input
              type="text"
              className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: 128"
              value={panna}
              onChange={handlePannaChange}
              maxLength="3"
            />
          </div>

          {/* Digit */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-600 mb-1">Digit</label>
            <input
              type="text"
              className="w-full border border-gray-300 px-3 py-2 rounded-lg bg-gray-100 font-bold text-center"
              value={digit}
              readOnly
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleShowWinners}
            className="w-full sm:w-auto bg-yellow-500 text-white font-semibold px-8 py-3 rounded-lg shadow-md hover:bg-yellow-600 transition"
            disabled={loading}
          >
            {loading ? "Loading..." : "Show Winners"}
          </button>

          <button
            onClick={handleDeclareResult}
            className="w-full sm:w-auto bg-blue-600 text-white font-semibold px-8 py-3 rounded-lg shadow-md hover:bg-blue-700 transition"
            disabled={declaring}
          >
            {declaring ? "Declaring..." : "Declare Result"}
          </button>
        </div>
      </div>

      {/* --- ✅ MODAL FIX: Z-Index 9999 & Fixed Position (Sidebar ke upar aayega) --- */}
      {showWinnerModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col animate-fadeIn">
            
            {/* Modal Header */}
            <div className="flex justify-between items-center bg-gray-900 text-white p-4 rounded-t-xl">
              <h3 className="text-lg font-bold">
                 Winning List: <span className="text-yellow-400">{selectedGame}</span>
              </h3>
              <button onClick={() => setShowWinnerModal(false)} className="text-2xl font-bold hover:text-red-400">&times;</button>
            </div>

            {/* Modal Body */}
            <div className="p-4 overflow-y-auto flex-1">
              <div className="flex justify-end mb-4">
                 <input
                  type="text"
                  className="border px-3 py-1.5 rounded-md text-sm w-full sm:w-64"
                  placeholder="Search winner..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* Responsive Table */}
              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">#</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Points</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Number</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Win Amt</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Time</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {bidHistoryData.length === 0 ? (
                      <tr><td colSpan="8" className="p-6 text-center text-gray-500">No winners found.</td></tr>
                    ) : (
                      bidHistoryData
                      .filter(b => (b.userName || "").toLowerCase().includes(search.toLowerCase()))
                      .map((bid, index) => {
                        const amountToShow = bid.winningAmount ?? bid.winning_amount ?? bid.amount ?? 0;
                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-sm">{index + 1}</td>
                            <td className="px-4 py-2 text-sm font-medium">{bid.userName || "User"}</td>
                            <td className="px-4 py-2 text-sm">{bid.gametype}</td>
                            <td className="px-4 py-2 text-sm">{bid.points}</td>
                            <td className="px-4 py-2 text-sm font-bold text-blue-600">{bid.digit || bid.panna}</td>
                            <td className="px-4 py-2 text-sm font-bold text-green-600">₹{amountToShow}</td>
                            <td className="px-4 py-2 text-sm">{bid.time}</td>
                            <td className="px-4 py-2 text-center">
                              <button onClick={() => handleDeleteBid(bid.bidId)} className="text-red-500 bg-red-100 p-2 rounded hover:bg-red-200" disabled={deletingBid === bid.bidId}>
                                {deletingBid === bid.bidId ? "..." : "Delete"}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-100 p-3 flex justify-end rounded-b-xl">
              <button onClick={() => setShowWinnerModal(false)} className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* --- RESULT HISTORY SECTION --- */}
      <div className="bg-white p-4 md:p-6 shadow-lg rounded-xl">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <h3 className="text-xl font-bold text-gray-800 border-l-4 border-purple-600 pl-3">Result History</h3>
          <div className="flex gap-2 items-center w-full md:w-auto">
            <span className="text-sm font-medium whitespace-nowrap">Date:</span>
            <input
              type="date"
              className="w-full border border-gray-300 px-3 py-1.5 rounded text-sm"
              value={gameResultDate}
              onChange={(e) => { setGameResultDate(e.target.value); setCurrentPage(1); }}
            />
          </div>
        </div>

        {/* History Table */}
        {loadingGameResult ? (
          <p className="text-center p-4">Loading history...</p>
        ) : (
          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">S.No</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Game Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Panna</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Digit</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Winners</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedResults.length === 0 ? (
                  <tr><td colSpan="7" className="p-6 text-center text-gray-500">No results found for this date.</td></tr>
                ) : (
                  paginatedResults.map((result, index) => (
                    <tr key={index} className="hover:bg-purple-50">
                      <td className="px-4 py-3 text-sm">{(currentPage - 1) * entriesPerPage + index + 1}</td>
                      <td className="px-4 py-3 text-sm font-medium">{result.market}</td>
                      <td className="px-4 py-3 text-sm">{result.panna || "-"}</td>
                      <td className="px-4 py-3 text-sm font-bold">{result.digit}</td>
                      <td className="px-4 py-3 text-sm"><span className="bg-green-100 text-green-800 px-2 py-0.5 rounded">{result.totalWinners} Winners</span></td>
                      <td className="px-4 py-3 text-sm">{result.date}</td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => handleDeleteGameResult(result.id)} className="text-red-600 bg-red-50 px-3 py-1 rounded text-xs">Delete</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {filteredGameResults.length > entriesPerPage && (
          <div className="flex justify-between items-center mt-4">
            <button className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 text-sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
            <span className="text-sm">Page {currentPage} of {Math.ceil(filteredGameResults.length / entriesPerPage)}</span>
            <button className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 text-sm" disabled={currentPage === Math.ceil(filteredGameResults.length / entriesPerPage)} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeclareResult;